import React, { useContext, useEffect, useState } from 'react';
import {
  Datagrid,
  TextInput,
  Resource,
  TextField,
  Filter as RAFilter,
  SelectInput,
  EditButton,
  NullableBooleanInput,
  SimpleFormIterator,
  ArrayInput,
  ReferenceInput,
  AutocompleteInput,
  FormDataConsumer,
  useGetOne,
  NumberInput,
  SelectArrayInput,
  ReferenceField,
  TabbedForm,
  FormTab,
} from 'react-admin';
import { AppCreate, AppEdit, AppList } from 'components/CRUD';
import Icon from '@material-ui/icons/Tune';
import axios from 'axios';
import { HostContext } from 'App';
import { Box, Paper, Typography } from '@material-ui/core';
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';

const Filter = props => (
  <RAFilter {...props}>
    <TextInput source="name" alwaysOn />
    <TextInput source="assetTag" alwaysOn />
    <TextInput source="building" />
    <TextInput source="room" />
    <SelectInput alwaysOn source='status' choices={[
        {id: 'registered', name: 'Registered'},
        {id: 'denied', name: 'Denied'},
        {id: 'approved', name: 'Approved'},
      ]} />
      <ReferenceInput source='customer' reference='Customer' alwaysOn perPage={1000} sort={{field: 'name', order: 'ASC'}}>
        <SelectInput optionText='name' />
      </ReferenceInput>
    <TextInput source="auth0.organizationId" label="Org ID" alwaysOn />
  </RAFilter>
);

export const List = props => {
  return (
    <AppList {...props} filters={<Filter />}>
      <Datagrid>
        <TextField source='name' label='Name' />
        <TextField source='machineId' label='Serial' />
        <TextField source='assetTag' label='Asset Tag' />
        <ReferenceField source="customer" reference='Customer'>
          <TextField source='name' />
        </ReferenceField>
        <TextField source='building' label='Building' />
        <TextField source='room' label='Room' />
        <EditButton />
      </Datagrid>
    </AppList>
  )
}

const Primary = ({edit}) => (
  <Box width='100% !important' display='flex'>
    <Box width={0.5} display='flex' flexDirection={'column'} p={2}>
      <Box width={1} display='flex' flexDirection='row'>
        <Box width={0.5} pr={1}>
          <TextInput source='machineId' fullWidth label='Serial Number' disabled={edit} />
        </Box>
        <Box width={0.5} pl={1}>
          <TextInput source='assetTag' fullWidth label='Asset Tag' />
        </Box>
      </Box>
      <Box width={1} display='flex' flexDirection='row'>
        <TextInput source='name' fullWidth label='Name' />
      </Box>
      <Box width={1} display='flex' flexDirection='row'>
        <Box width={0.5} pr={1}>
          <SelectInput source='status' fullWidth choices={[
            {id: 'registered', name: 'Registered'},
            {id: 'denied', name: 'Denied'},
            {id: 'approved', name: 'Approved'},
          ]} />
        </Box>
        <Box width={0.5} pl={1}>
          <ReferenceInput reference='Customer' source='customer' perPage={1000} sort={{field: 'name', order: 'ASC'}}>
            <AutocompleteInput optionText='name' fullWidth /> 
          </ReferenceInput>
        </Box>
      </Box>
      <Box width={1} display='flex' flexDirection='row'>
        <Box width={0.5} pr={1}>
          <TextInput source='building' fullWidth label='Building' />
        </Box>
        <Box width={0.5} pl={1}>
          <TextInput source='room' fullWidth label='Room' />
        </Box>
      </Box>
      <ArrayInput source='activations'>
        <SimpleFormIterator inline>
          <TextInput style={{minWidth: 300}} source='name' label='Name'/>
          <ReferenceInput 
            reference='Customer' 
            source='customer' 
            label='Customer'
            perPage={1000} 
            sort={{field: 'name', order: 'ASC'}}
          >
            <SelectInput style={{minWidth: 300}} fullWidth optionText='name'/> 
          </ReferenceInput>
          <FormDataConsumer subscription={{ values: true }}>
            {({ scopedFormData, getSource }) => {
              if (!scopedFormData?.customer) {
                return null;
              }
              return <CustomerLoader customerId={scopedFormData?.customer} getSource={getSource} />
            }}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </Box>
    <Box width={0.5} px={2}>
      <Paper>
        <Box m={2} display='flex' flexDirection={'column'} alignItems='center'>
        <Typography variant='h6'>System Config</Typography>
          <Box width={1} display='flex'>
            <Box width={1/2} px={1}>
              <SelectInput fullWidth source='config.screens.count' label='Screen Count' defaultValue={6} choices={
                Array.from(Array(64)).map((_, i) => ({
                  id: i + 1, name: (i + 1)?.toString()
                }))
              } />
            </Box>
            <Box width={1/2} px={1}>
              <SelectInput fullWidth source='config.screens.orientation' label='Orientation' defaultValue='portrait' choices={[
                {id: 'portrait', name: 'Portrait'},
                {id: 'landscape', name: 'Landscape'},
              ]} />
            </Box>
          </Box>
          <Box width={1} display='flex'>
            <Box width={1/3} px={1}>
              <SelectInput fullWidth source='config.screens.resolution' label='Resolution' defaultValue='HD 1080' choices={[
                {id: 'HD 1080', name: 'HD 1080'},
                {id: 'UHD 2160', name: 'UHD 2160'},
              ]} />
            </Box>
            <Box width={1/3} px={1}>
              <FormDataConsumer subscription={{ values: true }}>
                {({ formData }) => { 
                  const choices = [];

                  choices.push({
                    id: `${formData?.config?.screens?.count}x1`,
                    name: `${formData?.config?.screens?.count}x1`
                  })

                  Array.from(Array(formData?.config?.screens?.count)).forEach((_, i) => {
                    if (formData?.config?.screens?.count % i === 0) {
                      choices.push({
                        id: `${i}x${formData?.config?.screens?.count / i}`,
                        name: `${i}x${formData?.config?.screens?.count / i}`
                      })
                    }
                  });

                  return (
                    <SelectInput fullWidth source='config.screens.arrangement' label='Arrangement' choices={choices} defaultValue={choices[0]?.id} />
                  )
                }}
              </FormDataConsumer>
            </Box>
            <Box width={1/3} px={1}>
              <NumberInput fullWidth source='config.screens.scale' label='Scale' defaultValue={1} />
            </Box>
          </Box>
          <Box width={1} display='flex'>
            <Box width={0.25} px={1}>
              <NumberInput fullWidth source='config.screens.screenOffset.x' label='Screen Offset X' defaultValue={0} />
            </Box>
            <Box width={0.25} px={1}>
              <NumberInput fullWidth source='config.screens.screenOffset.y' label='Screen Offset Y' defaultValue={0} />
            </Box>
            <Box width={0.5} px={1}>
              <FormDataConsumer subscription={{ values: true }}>
                {({ formData }) => { 
                  const orientation = formData?.config?.screens?.orientation;
                  const resolution = formData?.config?.screens?.resolution;

                  const screenParts = formData?.config?.screens?.arrangement?.split('x') || [0, 0];
                  const size = {
                    width: orientation === 'landscape' ? (resolution === 'UHD 2160' ? 3840 : 1920) : (resolution === 'UHD 2160' ? 2160 : 1080),
                    height: orientation === 'landscape' ? (resolution === 'UHD 2160' ? 2160 : 1080) : (resolution === 'UHD 2160' ? 3840 : 1920),
                  }

                  return (
                  <>
                    <Typography variant='body1'>Cols: {screenParts[0]}</Typography>
                    <Typography variant='body1'>Rows: {screenParts[1]}</Typography>
                    <Typography variant='body1'>{`Resolution: (${size?.width * screenParts[0]}x${size?.height * screenParts[1]})`}</Typography>
                  </>
                  )
                }}
              </FormDataConsumer>
            </Box>
          </Box>
          <Box width={1} display='flex'>
            <Box width={0.25} px={1}>
              <NumberInput fullWidth source='config.screens.overscan.top' label='Overscan Top' defaultValue={0}/>
            </Box>
            <Box width={0.25} px={1}>
              <NumberInput fullWidth source='config.screens.overscan.right' label='Overscan Right'  defaultValue={0}/>
            </Box>
            <Box width={0.25} px={1}>
              <NumberInput fullWidth source='config.screens.overscan.bottom' label='Overscan Bottom'  defaultValue={0}/>
            </Box>
            <Box width={0.25} px={1}>
              <NumberInput fullWidth source='config.screens.overscan.left' label='Overscan Left'  defaultValue={0}/>
            </Box>
          </Box>
          {/* <Box width={1} display='flex'>
            <Box width={1} px={1}>
              <TextInput fullWidth source='config.azure.serviceBus' label='Service Bus'/>
            </Box>
          </Box> */}
          <Box width={1} display='flex'>
            <Box px={1} width={1/3}>
              <NullableBooleanInput source='config.analytics' label='Analytics' defaultValue={0} />
            </Box>
            <Box px={1} width={1/3}>
              <NullableBooleanInput source='config.broadcastBonjour' label='Enable Bonjour' defaultValue={false}/>
            </Box>
            <Box px={1} width={1/3}>
              <NullableBooleanInput source='config.openDevToolsOnStart' label='Dev Tools' defaultValue={false}/>
            </Box>
          </Box>
          <Box width={1} display='flex'>
            <Box px={1}>
              <NullableBooleanInput source='config.appMenu.show' label='App Menu' defaultValue={0} />
            </Box>
          </Box>
          <Box width={1} display='flex'>
            <Box px={1}>
              <FormDataConsumer subscription={{ values: true }}>
                {({ formData }) => {
                  if (!formData?.config?.appMenu?.show) {
                    return null;
                  }
                  return (
                    <SelectArrayInput style={{minWidth: 300}} m label='Position' source='config.appMenu.positions' choices={[
                      {id: 'top-left', name: 'Top Left'},
                      {id: 'top-right', name: 'Top Right'},
                      {id: 'bottom-left', name: 'Bottom Left'},
                      {id: 'bottom-right', name: 'Bottom Right'},
                    ]}
                    />
                  )
                }}
              </FormDataConsumer>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  </Box>
);

const Settings = props => {
  return (
    <Box width='100% !important' display='flex'>
      <ArrayInput source='settings'>
        <SimpleFormIterator inline>
          <SelectInput source='key' choices={[
            {id: 'proxy_pass_api_key', name: 'Client Data Proxy Service API Key'}
          ]} helperText={false} label='Key' />
          <TextInput source='value' helperText={false} label='Value' />
        </SimpleFormIterator>
      </ArrayInput>
    </Box>
  )
};

export const Edit = props => (
  <AppEdit {...props} undoable={false}>      
    <TabbedForm warnWhenUnsavedChanges>
      <FormTab label='General'>
        <Primary edit />
      </FormTab>
      <FormTab label='Settings'>
        <Settings edit />
      </FormTab>
    </TabbedForm>
  </AppEdit>
);

export const Create = props => (
  <AppCreate {...props} undoable={false}> 
    <TabbedForm warnWhenUnsavedChanges>
      <FormTab label='General'>
        <Primary />
      </FormTab>
      <FormTab label='Settings'>
        <Settings />
      </FormTab>
    </TabbedForm>
  </AppCreate>
);

const CustomerLoader = ({customerId, getSource}) => {
  const {data: customer} = useGetOne('Customer', customerId);

  if (!customer) {
    return <span>Cannot load customer</span>;
  }

  return <CustomerLocationSelector customer={customer} getSource={getSource} />;
}

const CustomerLocationSelector = ({customer, getSource}) => {
  const [locations, setLocations] = useState([]);
  const {apiHost} = useContext(HostContext);

  useEffect(() => {
    const run = async () => {
      const response = await axios.get(`${apiHost}/api/app-data/locations?deleted=false&enabled=true`, {
        headers: {
          'x-org-id': customer?.auth0?.organizationId,
        }
      });

      if (response?.status !== 200) {
        return;
      }

      setLocations(response?.data?.data?.map(row => ({id: row?._id, name: row?.name})));
    }

    run();
  }, [customer, apiHost]);

  return (
    <SelectInput style={{minWidth: 300}} source={getSource('locationId')} choices={locations} label='Location' />
  );
}


const Admin = () => <Resource name="Device" options={{label: 'Devices'}} create={Create} list={List} edit={Edit} icon={Icon} />;

export default Admin;